import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import JSZip from 'jszip';
import webrtcLoad from './video';
import Carousel from 'better-react-carousel';

const VideoRecorder = forwardRef(({ handlefile, streamurl }, ref) => {
  const [timer, setTimer] = useState(0);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [recordedVideoURLs, setRecordedVideoURLs] = useState([]);
  const [snapshotURLs, setSnapshotURLs] = useState([]);

  useEffect(() => {
    if (recording) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [recording]);

  useEffect(() => {
    webrtcLoad(streamurl);
  }, [streamurl]);

  const startRecording = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const stream = videoRef.current.captureStream();
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/mp4' });
        const videoUrl = URL.createObjectURL(blob);
        setRecordedVideoURLs(prevURLs => [...prevURLs, videoUrl]);
      };

      mediaRecorderRef.current.start();
      setRecording(true);
    } catch (error) {
      console.error('Error in startRecording:', error);
    }
  };

  const stopRecording = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
      setTimer(0);
    }
  };

  const captureSnapshot = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const context = canvas.getContext('2d');
    context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const snapshotUrl = canvas.toDataURL('image/jpg');
    setSnapshotURLs(prevURLs => [...prevURLs, snapshotUrl]);
  };

  const createAndReturnZip = async () => {
    console.log('Creating zip from:', {
      videos: recordedVideoURLs.length,
      snapshots: snapshotURLs.length
    });
  
    if (recordedVideoURLs.length === 0 && snapshotURLs.length === 0) {
      return null;
    }
  
    try {
      const zip = new JSZip();
      const timestamp = new Date().getTime();
      const outerFolderName = `device_recordings_${timestamp}`; 
      const innerFolderName = `device_recordings`;
      
      const folder = zip.folder(outerFolderName)
  
      for (let i = 0; i < recordedVideoURLs.length; i++) {
        console.log(`Processing video ${i}`);
        const response = await fetch(recordedVideoURLs[i]);
        const blob = await response.blob();
        folder.file(`video_${i + 1}.mp4`, blob);
      }
  
      for (let i = 0; i < snapshotURLs.length; i++) {
        console.log(`Processing snapshot ${i}`);
        const response = await fetch(snapshotURLs[i]);
        const blob = await response.blob();
        folder.file(`snapshot_${i + 1}.jpg`, blob);
      }
  
      console.log('Generating final zip');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      zipBlob.name = `${outerFolderName}.zip`;
      console.log('Zip created:', zipBlob.name);
      return zipBlob;
    } catch (error) {
      console.error('Error creating zip:', error);
      return null;
    }
  };
  useImperativeHandle(ref, () => ({
    createZip: createAndReturnZip
  }));

  const formatTime = (seconds) => {
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  return (
    <div className="row">
      <div className="col-md-12">
        <video id="video" className="mb-3 w-100" ref={videoRef} autoPlay></video>
        {timer > 0 && <h5 className="w-100 text-center my-2">{formatTime(timer)}</h5>}
        
        <div className="d-flex justify-content-center">
          {!recording ? (
            <button type="button" className="btn btn-light bg-light px-1" onClick={startRecording}>
              <span className="h4 fw-bold p-0 m-0 bg-light">
                <img src='/ico/rec-button.png' style={{width: '32px', cursor:'pointer'}} alt="Record" />
              </span>
            </button>
          ) : (
            <button type="button" className="btn btn-light bg-light px-1" onClick={stopRecording}>
              <span className="h4 fw-bold p-0 m-0 bg-light">
                <img src='/ico/icons8-stop-64.png' style={{width: '32px', cursor:'pointer'}} alt="Stop" />
              </span>
            </button>
          )}

          &nbsp;&nbsp;&nbsp;&nbsp;
          <button type="button" className="btn btn-light bg-light px-1" onClick={captureSnapshot}>
            <span className="h4 fw-bold p-0 m-0 bg-light">
              <img src='/ico/capture.png' style={{width: '32px', cursor:'pointer'}} alt="Capture" />
            </span>
          </button>
        </div>

        {(recordedVideoURLs.length + snapshotURLs.length > 3) ? (
          <div>   
            <Carousel cols={4} rows={1} gap={10} loop>
              {recordedVideoURLs.map((videoUrl, index) => (
                <Carousel.Item key={`video-${index}`}>
                  <p>Recorded Video {index + 1}:</p>
                  <video controls width={200} src={videoUrl}></video>
                </Carousel.Item>
              ))}
              {snapshotURLs.map((snapshotUrl, index) => (
                <Carousel.Item key={`snapshot-${index}`}>
                  <p>Snapshot {index + 1}:</p>
                  <img src={snapshotUrl} alt={`Snapshot ${index + 1}`} width={200} />
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        ) : (
          <div style={{display:'flex', justifyContent:'space-evenly'}}>
            {recordedVideoURLs.map((videoUrl, index) => (
              <div key={`video-${index}`}>
                <p>Recorded Video {index + 1}:</p>
                <video controls width={200} src={videoUrl}></video>
              </div>
            ))}
            {snapshotURLs.map((snapshotUrl, index) => (
              <div key={`snapshot-${index}`}>
                <p>Snapshot {index + 1}:</p>
                <img src={snapshotUrl} alt={`Snapshot ${index + 1}`} width={200} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

export default VideoRecorder;
